import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { ModulesResponse, ModulesQueryParams } from '../types';

export const getModules = (
  params: ModulesQueryParams = {}
): Promise<ModulesResponse> => {
  return axios.get('/user/v3/modules', { params });
};

const randomStatus = () =>
  faker.helpers.arrayElement([
    'PAID_COMPLETED',
    'PAID_ONGOING',
    'PAID_UPCOMING',
    'UNPAID',
  ] as const);

const generateReadableDate = (date: Date): string => {
  const day = date.toLocaleDateString('en-US', { day: '2-digit' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  return `${day} ${month}, (${weekday})`;
};

const generateRandomModulesResponse = (): ModulesResponse => {
  const numModules = faker.number.int({ min: 4, max: 8 });

  const modules = Array.from({ length: numModules }).map((_, index) => {
    const moduleId = faker.string.uuid();
    const moduleName = faker.commerce.productName();
    const sessionCount = faker.number.int({ min: 5, max: 8 });

    const sessions = Array.from({ length: sessionCount }).map(
      (_, sessionIndex) => {
        const date = faker.date.recent({ days: 30 });
        return {
          id: faker.string.uuid(),
          component: 'session_list_item' as const,
          name: faker.company.buzzPhrase(),
          number: sessionIndex + 1,
          delivery_type: 'offline' as const,
          topics: [
            {
              id: faker.string.uuid(),
              name: faker.hacker.noun(),
              worksheets: [
                {
                  id: faker.string.uuid(),
                  name: faker.commerce.product(),
                  label: faker.commerce.productAdjective(),
                  topic: {
                    id: faker.string.uuid(),
                    name: faker.commerce.department(),
                  },
                  description: faker.lorem.sentence(),
                  question_pdf_s3_url: faker.internet.url(),
                  answer_pdf_s3_url: faker.internet.url(),
                  answer_video_s3_url: faker.internet.url(),
                  worksheet_number: faker.number.int({ min: 1, max: 10 }),
                },
              ],
            },
          ],
          concepts: [
            { id: faker.string.uuid(), name: faker.hacker.verb() },
            { id: faker.string.uuid(), name: faker.hacker.verb() },
          ],
          video: {
            type: 'url' as const,
            url: faker.internet.url(),
            play_url: faker.internet.url(),
          },
          date: {
            timestamp: date.getTime(),
            readable: generateReadableDate(date),
          },
          attendance: {
            status: faker.helpers.arrayElement(['ATTENDED', 'MISSED'] as const),
          },
          session_type: faker.helpers.arrayElement([
            'Batch Session',
            'Demo Session',
          ]),
        };
      }
    );

    const worksheets = Array.from({
      length: faker.number.int({ min: 3, max: 6 }),
    }).map((_, wsIndex) => ({
      id: faker.string.uuid(),
      name: wsIndex.toString() + faker.commerce.productName(),
      label: faker.commerce.productAdjective(),
      module: faker.helpers.maybe(() => ({
        id: faker.string.uuid(),
        name: faker.commerce.productName(),
      })),
      topic: faker.helpers.maybe(() => ({
        id: faker.string.uuid(),
        name: faker.commerce.productName(),
      })),
      description: faker.lorem.sentence(),
      question_pdf_s3_url: faker.internet.url(),
      answer_pdf_s3_url: faker.internet.url(),
      answer_video_s3_url: faker.internet.url(),
      session_ids: Array.from({
        length: faker.number.int({ min: 0, max: 5 }),
      }).map(() => faker.string.uuid()),
      aggregated_score: faker.helpers.maybe(() =>
        faker.number.int({ min: 0, max: 100 })
      ),
      total_marks: 100,
      worksheet_type: faker.helpers.arrayElement(['PRE', 'POST']),
      worksheet_number: wsIndex + 1,
    }));

    return {
      message: 'Module found' as const,
      module: {
        component: faker.helpers.arrayElement([
          'module-item',
          'module_item',
          'module_item_on_demand',
        ] as const),
        id: moduleId,
        name: moduleName,
        status: randomStatus(),
        number: index.toString(),
        progress: faker.datatype.boolean()
          ? {
              component: 'metric_progress' as const,
              label: 'Progress',
              total: 100,
              completed: faker.number.int({ min: 0, max: 100 }),
              icon: null,
              value: null,
              type: 'progress' as const,
            }
          : null,
        outcomes: faker.datatype.boolean()
          ? [faker.company.buzzPhrase(), faker.company.buzzPhrase()]
          : null,
        is_optional: faker.datatype.boolean(),
        banner: { type: 'url' as const, url: 'https://picsum.photos/1280/720' },
      },
      sessions,
      worksheets,
    };
  });

  const on_demand = Array.from({
    length: faker.number.int({ min: 2, max: 3 }),
  }).map(() => ({
    module_id: faker.string.uuid(),
    name: faker.commerce.productName(),
    description: faker.lorem.sentence(),
    topics: Array.from({
      length: faker.number.int({ min: 4, max: 7 }),
    }).map(() => ({
      id: faker.string.uuid(),
      name: faker.commerce.product(),
      description: faker.lorem.sentence(),
      batch_type: faker.helpers.arrayElement([
        'regular',
        'intensive',
        'weekend',
      ]),
      grade: faker.helpers.arrayElement(['UKG-1', 'Grade 1', 'Grade 2']),
      module_id: faker.string.uuid(),
      topic_type: faker.helpers.arrayElement([
        'concept',
        'practice',
        'assessment',
      ]),
      recording: {
        manifest_path: faker.system.filePath(),
        video_duration: faker.number.int({ min: 300, max: 3600 }),
      },
      worksheet_details: Array.from({
        length: faker.number.int({ min: 0, max: 2 }),
      }).map((_, index) => ({
        id: faker.string.uuid(),
        name: faker.commerce.productName(),
        description: faker.lorem.sentence(),
        question_pdf_s3_url: faker.internet.url(),
        answer_pdf_s3_url: faker.internet.url(),
        answer_video_s3_url: faker.internet.url(),
        worksheet_number: index + 1,
      })),
    })),
  }));

  const course_aggregate = {
    grade: faker.helpers.arrayElement(['UKG-1', 'Grade 1', 'Grade 2']),
    name: faker.commerce.productName(),
    course_code: `${faker.number.int({ min: 1, max: 3 })}-${faker.number.int({ min: 50, max: 200 })}`,
    metrics: [
      {
        component: 'metric_progress' as const,
        label: 'Session status',
        total: 150,
        completed: faker.number.int({ min: 0, max: 150 }),
        icon: null,
        value: null,
        type: 'progress' as const,
      },
      {
        component: 'metric_item' as const,
        label: 'Module(s) completed',
        total: null,
        completed: null,
        icon: 'AutoAwesomeMosaicOutlinedIcon',
        value: faker.number.int({ min: 0, max: 10 }),
        type: 'item' as const,
      },
    ],
  };

  return { modules, on_demand, course_aggregate } satisfies ModulesResponse;
};

export const modulesMockHandler = http.get(
  `${env.API_URL}/user/v3/modules`,
  async (): Promise<HttpResponse<ModulesResponse>> => {
    await networkDelay();
    const response = generateRandomModulesResponse();
    return HttpResponse.json(response);
  }
);
